import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import Head from "next/head";
import { VFC } from "react";
import { SeoContent } from "types/graphql/generated";

interface Props {
  content: SeoContent;
}

const Seo: VFC<Props> = withErrorBoundary(
  ({ content: { metaTitle, metaDescription, canonicalUrl, sharedImage, preventIndexing, structuredData } }) => {
    const { media, alt } = sharedImage;
    const image = media.data?.attributes.url.split(".svg").join(".jpg");

    const data = structuredData ? JSON.stringify(structuredData) : null;

    return (
      <Head key="pageKey">
        {metaTitle ? <title>{metaTitle}</title> : null}
        {metaDescription ? <meta name="description" content={metaDescription} key="description" /> : null}
        <meta name="twitter:card" content="summary_large_image" key="twitter:card" />
        {canonicalUrl ? <meta property="og:url" content={canonicalUrl} key="og:url" /> : null}
        {metaTitle ? <meta property="og:title" content={metaTitle} key="og:title" /> : null}
        {metaDescription ? <meta property="og:description" content={metaDescription} key="og:description" /> : null}
        {image && (
          <>
            <meta property="og:image" content={image} key="og:image" />
            <meta property="og:image:alt" content={alt || media.data?.media.alt} key="og:image:alt" />
          </>
        )}
        {preventIndexing ? (
          <>
            <meta name="robots" content="noindex" />
            <meta name="googlebot" content="noindex" />
          </>
        ) : null}
        {canonicalUrl ? <link rel="canonical" href={canonicalUrl} key="canonicalUrl" /> : null}
        {data ? <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: data }} /> : null}
      </Head>
    );
  },
  {}
);

gql`
  fragment SeoContent on ComponentSeoSeo {
    metaTitle
    metaDescription
    preventIndexing
    structuredData
    canonicalUrl
    sharedImage {
      alt
      media {
        data {
          media: attributes {
            alt: alternativeText
          }
          attributes {
            url
          }
        }
      }
    }
  }
`;

export { Seo };
export type { Props as SeoProps };
